/**
 * 仓库制品（PIKE-RAG warehouse-artifact）图片渲染。
 *
 * SSE references 片段（references_segment）里的图片是很短的相对路径，形如：
 *   ![slide07_img_016.png](assets/images/slide07_img_016.png)
 * 浏览器无法直接预览，且 <img> 不能携带本系统的 JWT（不能走 Cookie），
 * 所以这里用 fetch 带 Authorization 头请求后端中转接口，拿到 blob 后用 object URL 渲染。
 *
 * 后端中转：GET /api/project/documentlist/warehouseartifact?documentId=..&path=..
 *   - documentId = 该条引用的 references_document_id（上游 assetId / owner 文档 ID）
 *   - path       = markdown 里的相对路径（本组件原样转发，版本前缀由后端兜底补全）
 * 详见后端 docs/v3-warehouse-artifact-api.md。
 */
import React, { useEffect, useState } from "react";
import request, { TOKEN_KEY } from "./request";

/** 拼后端中转接口 URL（不含鉴权头，鉴权头在 fetch 时附加） */
function buildWarehouseArtifactUrl(documentId: string, path: string): string {
  const baseURL = (request.defaults?.baseURL as string | undefined) || "/api";
  const url = `${baseURL.replace(/\/$/, "")}/project/documentlist/warehouseartifact`;
  const qs = new URLSearchParams({ documentId, path });
  return `${url}?${qs.toString()}`;
}

/**
 * 仅对「需要中转的仓库制品相对路径」走代理；外链 / data: / blob: / 绝对路径原样渲染。
 */
function isProxyableArtifactPath(src?: string): boolean {
  if (!src) return false;
  if (/^(https?:|data:|blob:|\/\/)/i.test(src)) return false;
  if (src.startsWith("/")) return false;
  return true;
}

// ── 会话级缓存：同一 (documentId, path) 只拉取一次，避免重复 hover/重渲染反复请求与 object URL 泄漏 ──
const artifactUrlCache = new Map<string, string>();
const artifactInflight = new Map<string, Promise<string>>();

async function loadArtifactObjectUrl(documentId: string, path: string): Promise<string> {
  const key = `${documentId}::${path}`;
  const cached = artifactUrlCache.get(key);
  if (cached) return cached;
  const inflight = artifactInflight.get(key);
  if (inflight) return inflight;

  const task = (async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const resp = await fetch(buildWarehouseArtifactUrl(documentId, path), {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    artifactUrlCache.set(key, objUrl);
    return objUrl;
  })();

  artifactInflight.set(key, task);
  try {
    return await task;
  } finally {
    artifactInflight.delete(key);
  }
}

const placeholderStyle: React.CSSProperties = {
  color: "rgba(0,0,0,0.45)",
  fontSize: 12,
};

const imgStyle: React.CSSProperties = {
  maxWidth: "100%",
  height: "auto",
  display: "block",
  margin: "8px 0",
};

interface WarehouseImageProps {
  src?: string;
  alt?: string;
  /** 该图片所属引用的 documentId（上游 assetId）；缺失时无法中转，退化为原样渲染 */
  documentId?: string;
}

/** 仓库制品图片：通过后端中转 + JWT 拉取 blob 后渲染 */
export const WarehouseImage: React.FC<WarehouseImageProps> = ({ src, alt, documentId }) => {
  const proxyable = isProxyableArtifactPath(src) && !!documentId;
  const [objUrl, setObjUrl] = useState<string>(() =>
    proxyable ? (artifactUrlCache.get(`${documentId}::${src}`) ?? "") : "",
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!proxyable) return;
    if (objUrl) return;
    let cancelled = false;
    setFailed(false);
    loadArtifactObjectUrl(documentId as string, src as string)
      .then((url) => {
        if (!cancelled) setObjUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // object URL 进会话级缓存、不在卸载时回收，避免 hover popover 反复创建/销毁导致闪烁
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyable, documentId, src]);

  // 非仓库制品（外链等）或缺 documentId：原样渲染
  if (!proxyable) {
    return <img src={src} alt={alt} style={imgStyle} />;
  }
  if (failed) {
    return <span style={placeholderStyle}>[Image failed to load] {alt || src}</span>;
  }
  if (!objUrl) {
    return <span style={placeholderStyle}>Loading image…</span>;
  }
  return <img src={objUrl} alt={alt} style={imgStyle} />;
};
